<template>
  <div style="width: 100%">
    <div class="readiness-container">
      <AsACoachHeader class="page-title" />
    </div>
    <div v-if="hasInitialLoad">
      <div class="readiness-container">
        <EmployeeReadinessMenu :items="menuItems" />
      </div>
      <v-tabs-items v-model="selectedTab" class="custom-tabs">
        <v-tab-item v-for="(item, index) in menuItems" :key="index">
          <v-card elevation="0" class="employee-readiness-container" v-if="selectedTab === 0">
            <AsCoachView
              :dataSource="dataSource"
              :currentPage="currentPage"
              :pageSize="pageSize"
              :totalRecords="totalRecords"
              @pageChange="handlePageChange"
              @pageSizeChange="handlePageSizeChange"
              @search="handleSearch"
            />
          </v-card>
          <v-card v-if="selectedTab === 2"> </v-card>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </div>
</template>

<script>
import AsACoachHeader from "@components/AsACoachHeader/AsACoachHeader.vue";
import EmployeeReadinessMenu from "@components/EmployeeReadinessMenu/EmployeeReadinessMenu.vue";
import AsCoachView from "./AsCoach/AsCoachView.vue";
import { useViewState } from "./AsCoach/composables/useViewState";
import { getEmployeeCoachResponsibilitesV2 } from "@/api";
export default {
  components: {
    AsACoachHeader,
    EmployeeReadinessMenu,
    AsCoachView,
  },
  props: {},
  async mounted() {
    this.accountID = JSON.parse(localStorage.getItem("currentAccountID"));
    this.authUser = JSON.parse(localStorage.getItem("user"));
    this.getCoachResponsibilities();
  },
  setup() {
    const viewState = useViewState();

    return {
      viewState,
    };
  },
  data() {
    return {
      dataSource: [],
      accountID: null,
      authUser: null,
      topLevel: "position",
      removeBorder: false,
      selectedTab: 0,
      currentReadiness: null,
      // Backend pagination
      currentPage: 1,
      pageSize: 10,
      totalRecords: 0,
      searchQuery: "",
      searchDebounce: null,
      isSearching: false,
      hasInitialLoad: false,
    };
  },
  computed: {
    menuItems() {
      const items = [];
      items.push({
        title: "Your Employees to Coach",
        count: this.totalRecords || this.dataSource?.length,
      });
      return items;
    },
  },
  watch: {
    "viewState.state.getCoachResponsibilties"(newVal) {
      if (newVal) {
        this.getCoachResponsibilities();
      }
    },
  },
  methods: {
    async getCoachResponsibilities() {
      try {
        this.$loader.show();
        const offset = (this.currentPage - 1) * this.pageSize;
        const { data } = await getEmployeeCoachResponsibilitesV2(
          this.accountID,
          this.pageSize,
          offset,
          this.searchQuery
        );

        // Keep responsibility count for proper backend pagination
        this.totalRecords = data.count || 0;
        this.hasInitialLoad = true;

        if (data.results.length > 0) {
          this.dataSource = this.transformData(data.results);
        } else {
          this.dataSource = [];
        }
      } catch (error) {
        console.error("Error fetching coach responsibilities:", error);
        this.dataSource = [];
      } finally {
        this.$loader.hide();
        this.viewState.state.getCoachResponsibilties = false;
      }
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.getCoachResponsibilities();
    },
    handlePageSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.getCoachResponsibilities();
    },
    handleSearch(query) {
      this.isSearching = query && query.trim() !== "";

      // Update viewState immediately for UI updates (like showing Clear Search button)
      this.viewState.state.filters.searchText = query;

      // Clear existing debounce timer
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
      }

      // Set new debounce timer for API call
      this.searchDebounce = setTimeout(() => {
        this.searchQuery = query;
        this.currentPage = 1;
        this.getCoachResponsibilities();
      }, 500); // 500ms debounce delay
    },
    transformData(dataArray) {
      // Flatten the data: create one row per coach per responsibility
      const flattened = [];

      dataArray.forEach((responsibility) => {
        // If there are coaches, create a row for each coach
        if (responsibility.coaches && responsibility.coaches.length > 0) {
          responsibility.coaches.forEach((coach) => {
            flattened.push({
              id: `${responsibility.id}-${coach.id}`,
              responsibilityId: responsibility.id,
              name: responsibility.name,
              code: this.extractCode(responsibility.code_str),
              employee: {
                id: coach.id,
                first_name: coach.name ? coach.name.split(" ")[0] : "",
                last_name: coach.name ? coach.name.split(" ").slice(1).join(" ") : "",
                title: "",
                avatar: coach.avatar,
              },
              badge: {
                level: coach.level,
                status: "active",
              },
              currentBadgeLevel: coach.level ? coach.level.toLowerCase() : "novice",
              currentBadgeStatus: "active",
              checkpoints: [],
              active_checkpoints: responsibility.active_checkpoints_count || 0,
              checkpoint_count:
                (responsibility.count_apprentice_checkpoints || 0) +
                (responsibility.count_professional_checkpoints || 0) +
                (responsibility.count_coach_checkpoints || 0) +
                (responsibility.count_master_checkpoints || 0),
              // Checkpoint counts for InspectionIssueBadge component
              checkpointCounts: {
                apprentice_checkpoint_count: responsibility.count_apprentice_checkpoints || 0,
                apprentice_passed_checkpoint_count: 0, // Not provided by API
                professional_checkpoint_count: responsibility.count_professional_checkpoints || 0,
                professional_passed_checkpoint_count: 0, // Not provided by API
                coach_checkpoint_count: responsibility.count_coach_checkpoints || 0,
                coach_passed_checkpoint_count: 0, // Not provided by API
                master_checkpoint_count: responsibility.count_master_checkpoints || 0,
                master_passed_checkpoint_count: 0, // Not provided by API
              },
            });
          });
        } else {
          // If no coaches, still create a row with empty employee data
          flattened.push({
            id: `${responsibility.id}-no-coach`,
            responsibilityId: responsibility.id,
            name: responsibility.name,
            code: this.extractCode(responsibility.code_str),
            employee: {
              id: null,
              first_name: "No",
              last_name: "Coach Assigned",
              title: "",
              avatar: null,
            },
            badge: {
              level: "Novice",
              status: "pending",
            },
            currentBadgeLevel: "novice",
            currentBadgeStatus: "pending",
            checkpoints: [],
            active_checkpoints: responsibility.active_checkpoints_count || 0,
            checkpoint_count:
              (responsibility.count_apprentice_checkpoints || 0) +
              (responsibility.count_professional_checkpoints || 0) +
              (responsibility.count_coach_checkpoints || 0) +
              (responsibility.count_master_checkpoints || 0),
            // Checkpoint counts for InspectionIssueBadge component
            checkpointCounts: {
              apprentice_checkpoint_count: responsibility.count_apprentice_checkpoints || 0,
              apprentice_passed_checkpoint_count: 0, // Not provided by API
              professional_checkpoint_count: responsibility.count_professional_checkpoints || 0,
              professional_passed_checkpoint_count: 0, // Not provided by API
              coach_checkpoint_count: responsibility.count_coach_checkpoints || 0,
              coach_passed_checkpoint_count: 0, // Not provided by API
              master_checkpoint_count: responsibility.count_master_checkpoints || 0,
              master_passed_checkpoint_count: 0, // Not provided by API
            },
          });
        }
      });

      return flattened;
    },
    extractCode(str) {
      let match = str?.match(/#\d+/);
      return match ? match[0] : null;
    },
    handleClearSearch() {
      this.searchQuery = "";
      this.isSearching = false;
      this.currentPage = 1;
      this.viewState.state.filters.searchText = "";
      this.getCoachResponsibilities();
    },
  },
  beforeDestroy() {
    // Clean up debounce timer
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
  },
};
</script>

<style lang="css" scoped>
.readiness-container {
  padding-left: 15px;
  width: 100%;
}

.page-title {
  margin-top: 16px;
}

.employee-path-to-readiness {
  /* box-shadow: 0px 1px 2px 0px #1018280d; */
  border-radius: 8px;
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 20px;
}
.employee-readiness-title {
  font-family: Inter;
  font-size: 18px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  color: #344054;
  min-width: 125px;
}
.employee-readiness {
  width: 100%;
  margin-left: 15px;
}
.employee-readiness .progress-bar-container {
  width: 100%;
}
.employee-readiness .progress-value {
  font-weight: 600;
}
.employee-path {
  border-radius: 8px;
}
::v-deep .progress-bar-container {
  width: 100%;
}
.employee-readiness-container {
  width: 100%;
  margin-left: 4px;
}

#treelist {
  max-width: 100%;
}

::v-deep(.v-tabs) {
  padding: 13px 0px !important;
  border-bottom: 1px solid #eaecf0 !important;
}

::v-deep(.custom-tabs) {
  top: 20px;
}
</style>
